import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {MoreHoriz} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import {Menu} from '@mui/material';

import {InviteApplicantModal} from '../components/InviteApplicantModal';
import {FilterOptions, ScreeningApplicant} from '../types/screeningExternalApplicantsTypes';

import {CopyLinkIcon} from '../../../../assets/icons';
import {ConfirmDeleteDialog} from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {EmpionActionMenuItems} from '../../../../components/EmpionActionMenuItems/EmpionActionMenuItems';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {useApiRead} from '../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';
import {Delete} from '../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../lib/api/DELETE/DeleteRoutes';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {CompanyAdminRoleData} from '../../../../types/User';
import {copyTextToClipBoard} from '../../../../utils/copyTextToClipboard/copyTextToClipBoard';

interface Props {
  applicantData: ScreeningApplicant;
  refetch: () => void;
}

export const ScreeningBurgerActions = ({applicantData, refetch}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editApplicant, setEditApplicant] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId || '';

  const {dispatchMessage} = useContext(SnackBarContext);
  const {t: tr} = useTranslation('translation', {keyPrefix: 'screeningModule.burgerActions'});
  const {t} = useTranslation('translation', {keyPrefix: 'shared'});

  const open = Boolean(anchorEl);
  const anchorOrigin: {vertical: 'bottom'; horizontal: 'right'} = {
    vertical: 'bottom',
    horizontal: 'right',
  };

  const transformOrigin: {vertical: 'top'; horizontal: 'right'} = {
    vertical: 'top',
    horizontal: 'right',
  };

  const menuStyles = {
    backgroundImage: 'none',
    '.MuiMenu-paper': {
      backgroundColor: orangeColors[7],
      borderRadius: '8px',
      padding: '16px',
      backgroundImage: 'none',
    },
  };

  const {data: filtersOptions, refetch: refetchFilters} = useApiRead<FilterOptions>({
    path: GetRoutes.screeningApplicantsFilterOptions(companyId),
    options: {
      enabled: !!companyId && editApplicant,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const {makeRequest: onDelete, error: onDeleteError} = useApiWrite({
    method: 'delete',
    path: DeleteRoutes[Delete.DeleteScreeningApplicant](companyId, applicantData.resourceId),
    onSuccess: () => {
      dispatchMessage(t('deleted'), 'success');
      setOpenDeleteDialog(false);
      setAnchorEl(null);
      refetch();
    },
  });

  useEffect(() => {
    if (onDeleteError) {
      dispatchMessage(t('formSubmit.deletionError'), 'error');
    }
  }, [onDeleteError, dispatchMessage, t]);

  const handleConfirmDelete = () => {
    onDelete();
  };

  const {makeRequest: resendInvitationEmail} = useApiWrite<Post.ResendExternalApplicantInvitation>({
    method: 'post',
    path: PostRoutes[Post.ResendExternalApplicantInvitation](companyId, applicantData.resourceId),
    onSuccess: () => {
      dispatchMessage(tr('resendInvitationSuccess'), 'success');
      refetch();
    },
    onError: () => {
      dispatchMessage(tr('resendInvitationError'), 'error');
    },
  });

  const handleCopyLink = useCallback(async () => {
    const res = await copyTextToClipBoard(`${applicantData.talentSurvey.surveyUrl}?source=screening_applicant`);
    if (res) {
      dispatchMessage(tr('coypLinkSuccess'), 'success');
    } else {
      dispatchMessage(tr('copyLinkError'), 'error');
    }
  }, [applicantData, dispatchMessage, tr]);

  const canResendInvitationEmail =
    !applicantData.talentSurvey.completedAt && Boolean(applicantData.email && applicantData.email.trim());

  const handleResendInvitationEmail = useCallback(() => {
    resendInvitationEmail();
  }, [resendInvitationEmail]);

  const menuItems = useMemo(
    () => [
      {
        key: 'copyLink',
        label: tr('copyLink'),
        onClick: handleCopyLink,
        icon: <CopyLinkIcon fill={greenColors[700]} style={{marginBottom: '4px'}} />,
      },
      ...(canResendInvitationEmail
        ? [
          {
            key: 'resendInvitation',
            label: tr('resendInvitation'),
            onClick: handleResendInvitationEmail,
            icon: <ForwardToInboxIcon style={{color: greenColors[700]}} />,
          },
        ]
        : []),
      {
        key: 'edit',
        label: tr('edit'),
        onClick: () => setEditApplicant(true),
        icon: <EditIcon style={{color: greenColors[700]}} />,
      },
      {
        key: 'delete',
        label: tr('delete'),
        onClick: () => setOpenDeleteDialog(true),
        icon: <DeleteIcon style={{color: greenColors[700]}} />,
      },
    ],
    [canResendInvitationEmail, tr, handleCopyLink, handleResendInvitationEmail]
  );

  return (
    <>
      <EmpionButton onClick={onClick} sx={{width: 'auto', minWidth: '32px', height: '32px', padding: 0}}>
        <MoreHoriz />
      </EmpionButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        keepMounted
        transformOrigin={transformOrigin}
        sx={menuStyles}
      >
        <EmpionActionMenuItems
          menuItems={menuItems}
          variant='body1'
          sx={{
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: orangeColors[3],
              width: '100%',
            },
          }}
        />
      </Menu>
      <InviteApplicantModal
        open={editApplicant}
        onClose={() => setEditApplicant(false)}
        data={applicantData}
        refetch={refetch}
        refetchFilters={refetchFilters}
        filterOptions={filtersOptions}
      />
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        handleCancel={() => setOpenDeleteDialog(false)}
        handleConfirm={handleConfirmDelete}
        dialogContent={tr('deleAction')}
      />
    </>
  );
};
